// /tenant/accept — public invite acceptance
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, AlertCircle, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const LANGS = [["en","English"],["es","Español"],["fr","Français"],["de","Deutsch"],["it","Italiano"],["pt","Português"],["zh","中文"],["ja","日本語"],["hi","हिन्दी"],["ar","العربية"]];

export default function TenantAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const token = params.get("token") || "";
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "", confirm: "", language: "en" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing token.");
      return;
    }
    api.get(`/auth/tenant/invite/${token}/preview`)
      .then(({ data }) => {
        setPreview(data);
        setForm((p) => ({ ...p, name: data.tenant_name || "", language: data.landlord_language || "en" }));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/tenant/accept", { token, name: form.name, password: form.password, language: form.language });
      toast.success("Welcome aboard!");
      await refresh?.();
      navigate("/tenant", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-indigo-50/40 to-white p-4">
        <div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50/40 p-6 text-center shadow-sm" data-testid="tenant-accept-loggedin">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-600" />
          <h2 className="mt-3 text-lg font-semibold text-slate-900">You're already signed in</h2>
          <p className="mt-1 text-sm text-slate-500">Log out first to accept this invite as a new tenant account.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4 bg-indigo-600 text-white hover:bg-indigo-700">Continue to dashboard</Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-indigo-50/40 to-white p-4">
        <div className="max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center shadow-sm" data-testid="tenant-accept-error">
          <AlertCircle className="mx-auto h-8 w-8 text-red-600" />
          <h2 className="mt-3 text-lg font-semibold text-slate-900">Invite unavailable</h2>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!preview) {
    return <div className="flex min-h-screen items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-indigo-50/40 to-white px-4 py-8">
      <div className="w-full max-w-md" data-testid="tenant-accept-page">
        <div className="mb-6 flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-8 w-8 rounded" />
          <span className="text-base font-bold tracking-tight">
            runidea
            <span className="ml-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700">Tenant</span>
          </span>
        </div>

        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-indigo-600" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">You've been invited</p>
          </div>
          <p className="mt-2 text-sm text-slate-800">
            <span className="font-semibold">{preview.landlord_name}</span> invited you to runidea.ai for
            <span className="font-semibold"> {preview.property_address}</span>, {preview.property_city} · {preview.property_country}.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Create your tenant account</h1>
          <div>
            <Label>Email</Label>
            <Input value={preview.tenant_email} disabled data-testid="tenant-accept-email" />
            <p className="mt-1 text-xs text-slate-500">Locked to the email on your invite.</p>
          </div>
          <div>
            <Label htmlFor="ta-name">Your name</Label>
            <Input id="ta-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="tenant-accept-name" />
          </div>
          <div>
            <Label htmlFor="ta-pw">Password (min 8 chars)</Label>
            <Input id="ta-pw" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={8} data-testid="tenant-accept-password" />
          </div>
          <div>
            <Label htmlFor="ta-pw2">Confirm password</Label>
            <Input id="ta-pw2" type="password" value={form.confirm} onChange={(e) => setForm({...form, confirm: e.target.value})} required data-testid="tenant-accept-confirm" />
          </div>
          <div>
            <Label>Preferred language</Label>
            <Select value={form.language} onValueChange={(v) => setForm({...form, language: v})}>
              <SelectTrigger data-testid="tenant-accept-language"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 text-white hover:bg-indigo-700" data-testid="tenant-accept-submit">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Accept invite & sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
